import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Plus, RefreshCw } from "lucide-react";
import { v4 } from "uuid";
import { ReactNode, useEffect, useState } from "react";
import { Slot } from "radix-ui";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useHtzOrderPutMutation } from "@/services/backend/htz/orders/api/order-api";
import { newOrder } from "@/services/backend/htz/orders/types/order";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { BranchSelectFrom } from "@/services/backend/branch/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import {
  CommonDialogFooter,
  CommonDialogHeader,
} from "@/shared/components/dialog/dialog-common";

interface Props {
  processId: string;
  onCreated: (orderId: string) => void;
  trigger?: ReactNode;
}

export function CreateOrderButton({ processId, onCreated, trigger }: Props) {
  const [open, setOpen] = useState(false);
  const { data: proc } = useProcessesProcessShowQuery({ id: processId });

  const [id, setId] = useState(v4());
  const [create, { error, isLoading, reset }] = useHtzOrderPutMutation();

  const branches = proc?.branches ?? [];
  const serviceObjects = proc?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  const disabled =
    !proc || isLoading || branchId === "" || serviceObjectId === "";

  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({ ...parseRTKQueryError(error), variant: "destructive" });
      reset();
      setId(v4());
    }
  }, [error, reset, toast]);

  const doCreate = () => {
    if (disabled) {
      return;
    }

    create(newOrder(id, proc, branchId, serviceObjectId))
      .unwrap()
      .then((o) => {
        setOpen(false);
        onCreated(o.id);
        reset();
        setId(v4());
      });
  };

  if (branches.length === 1 && serviceObjects.length === 1) {
    if (trigger) {
      return <Slot.Root onClick={doCreate}>{trigger}</Slot.Root>;
    }
    return (
      <Button onClick={doCreate} disabled={disabled} size="sm">
        {isLoading ? (
          <RefreshCw className="h-4 w-4 animate-spin" />
        ) : (
          <Plus className="h-5 w-5" />
        )}
        <span>{t("Auftragseingang")}</span>
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button disabled={disabled} size="sm">
            {isLoading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-5 w-5" />
            )}
            <span>{t("Auftragseingang")}</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <CommonDialogHeader title={t("Auftragseingang erstellen")} />
        {branches.length !== 1 && (
          <Field>
            <FieldLabel>{t("Niederlassung")}</FieldLabel>
            <BranchSelectFrom
              branchIds={branches}
              value={branchId}
              onValueChange={setBranchId}
            />
          </Field>
        )}
        {serviceObjects.length !== 1 && (
          <Field>
            <FieldLabel>{t("Service Objekt")}</FieldLabel>
            <ServiceObjectSelectFrom
              serviceObjectIds={serviceObjects}
              customerId={proc?.customerId}
              value={serviceObjectId}
              onValueChange={setServiceObjectId}
            />
          </Field>
        )}
        <CommonDialogFooter
          isLoading={isLoading}
          disabled={disabled}
          onClickAction={doCreate}
        />
      </DialogContent>
    </Dialog>
  );
}
